import type { Combatant } from './simulation'
import {
  animationSemanticForState,
  catalogueAnimationClips,
  type AnimationClipDescriptor,
  type AnimationMotionContext,
  type AnimationSemantic,
  type AnimationVariationPlan,
} from './animationVariation'
import type { MotorThoughtState } from './motionField'

export type ThoughtAnimationPlan = AnimationVariationPlan & {
  selectionMode: 'thought'
  thoughtSignature: string
  thoughtScore: number
  decisionMargin: number
  decisionConfidence: number
  continuityMode: 'hold'|'vary'|'switch'
  compatibleCount: number
  inventoryCount: number
  chosenSemantic: AnimationSemantic
}

export type ThoughtAnimationCandidate = AnimationClipDescriptor & {
  score: number
  affinity: number
  thoughtScore: number
  styleScore: number
}

const clamp=(n:number,lo=0,hi=1)=>Math.max(lo,Math.min(hi,Number.isFinite(n)?n:lo))
const signed=(n:number)=>Math.max(-1,Math.min(1,Number.isFinite(n)?n:0))
const attackSemantics=new Set<AnimationSemantic>(['light','heavy','special','throw','generic-attack'])
const locomotionSemantics=new Set<AnimationSemantic>(['idle','walk','backward','run','dash','depth'])
const airSemantics=new Set<AnimationSemantic>(['jump','air','flight','dive','land'])
const defenseSemantics=new Set<AnimationSemantic>(['block','parry'])

function hash32(input:string){
 let h=2166136261>>>0
 for(let i=0;i<input.length;i++){h^=input.charCodeAt(i);h=Math.imul(h,16777619)}
 h^=h>>>16;h=Math.imul(h,0x7feb352d);h^=h>>>15;h=Math.imul(h,0x846ca68b);h^=h>>>16
 return h>>>0
}
const unitHash=(input:string)=>hash32(input)/4294967295
const q=(n:number)=>Math.round(n*20).toString(36)

function normalizeThought(thought:Partial<MotorThoughtState>):MotorThoughtState{
 return{
  action:thought.action??'idle',speed01:clamp(thought.speed01??0),urgency:clamp(thought.urgency??0),
  arousal:clamp(thought.arousal??.2),confidence:clamp(thought.confidence??.5),fatigue:clamp(thought.fatigue??0),
  pain:clamp(thought.pain??0),curiosity:clamp(thought.curiosity??0),threat:clamp(thought.threat??0),
  socialWarmth:clamp(thought.socialWarmth??0),focus:clamp(thought.focus??.5),turn01:signed(thought.turn01??0),
  slope01:signed(thought.slope01??0),airborne01:clamp(thought.airborne01??0),
  contactIntent01:clamp(thought.contactIntent01??0),targetBearing01:signed(thought.targetBearing01??0),
 }
}
export function thoughtAnimationSignature(thought:Partial<MotorThoughtState>){
 const t=normalizeThought(thought)
 return[
  t.action,q(t.speed01),q(t.urgency),q(t.arousal),q(t.confidence),q(t.fatigue),q(t.pain),
  q(t.curiosity),q(t.threat),q(t.socialWarmth),q(t.focus),q(t.turn01),q(t.airborne01),
  q(t.contactIntent01),q(t.targetBearing01),
 ].join(':')
}

function compatible(desired:AnimationSemantic,candidate:AnimationSemantic){
 if(desired==='downed')return candidate==='downed'||candidate==='defeat'
 if(desired==='defeat')return candidate==='defeat'||candidate==='downed'
 if(desired==='getup')return candidate==='getup'||candidate==='idle'
 if(desired==='hit')return candidate==='hit'||candidate==='block'||candidate==='parry'
 if(desired==='victory')return candidate==='victory'||candidate==='idle'
 if(defenseSemantics.has(desired))return defenseSemantics.has(candidate)||candidate==='idle'
 if(attackSemantics.has(desired))return attackSemantics.has(candidate)
 if(airSemantics.has(desired))return airSemantics.has(candidate)||candidate==='idle'||candidate==='run'
 if(locomotionSemantics.has(desired))return locomotionSemantics.has(candidate)||candidate==='unknown'
 return candidate===desired
}

function semanticAffinity(desired:AnimationSemantic,candidate:AnimationSemantic){
 if(desired===candidate)return ['downed','defeat','getup','hit','victory'].includes(desired)?2:1.15
 if(attackSemantics.has(desired)&&attackSemantics.has(candidate))return .45
 if(airSemantics.has(desired)&&airSemantics.has(candidate))return .62
 if(locomotionSemantics.has(desired)&&locomotionSemantics.has(candidate))return .38
 if(defenseSemantics.has(desired)&&defenseSemantics.has(candidate))return .7
 if(candidate==='idle')return .18
 if(candidate==='unknown')return .08
 return 0
}
function semanticThoughtScore(semantic:AnimationSemantic,t:MotorThoughtState){
 switch(semantic){
  case 'special': return t.urgency*.9+t.arousal*.78+t.confidence*.32+t.contactIntent01*.62-t.fatigue*.22
  case 'heavy': return t.urgency*.72+t.arousal*.55+t.confidence*.42+t.contactIntent01*.5-t.pain*.18
  case 'light': return t.focus*.72+t.contactIntent01*.58+(1-t.fatigue)*.2+t.urgency*.18
  case 'throw': return t.focus*.56+t.contactIntent01*.72+t.threat*.22+t.confidence*.18
  case 'generic-attack': return t.urgency*.42+t.focus*.35+t.contactIntent01*.48+t.arousal*.24
  case 'dash': return t.threat*.72+t.urgency*.5+Math.abs(t.turn01)*.42+t.speed01*.38
  case 'depth': return Math.abs(t.turn01)*.72+t.threat*.34+t.focus*.25+t.speed01*.18
  case 'run': return t.speed01*.72+t.urgency*.28+(1-t.fatigue)*.14
  case 'walk': return t.speed01*.42+(1-t.urgency)*.2+(1-t.fatigue)*.12
  case 'backward': return t.threat*.48+t.focus*.36+t.speed01*.28+t.confidence*.12
  case 'jump': return t.airborne01*.62+t.urgency*.34+t.confidence*.18+t.speed01*.16
  case 'air': return t.airborne01*.74+t.focus*.26+(1-t.urgency)*.08
  case 'flight': return t.airborne01*.72+t.confidence*.28+t.focus*.2+t.speed01*.2
  case 'dive': return t.airborne01*.66+t.urgency*.62+t.arousal*.3+t.contactIntent01*.2
  case 'land': return (1-t.airborne01)*.58+t.focus*.26+t.contactIntent01*.16
  case 'idle': return (1-t.speed01)*.62+(1-t.arousal)*.24+t.confidence*.12
  case 'block': return t.threat*.72+t.focus*.26+(1-t.confidence)*.12
  case 'parry': return t.threat*.48+t.focus*.72+t.confidence*.22
  case 'hit': return t.pain*.58+t.threat*.38
  case 'downed': return t.pain*.52+t.fatigue*.52+(1-t.confidence)*.22
  case 'getup': return t.confidence*.5+(1-t.pain)*.28+(1-t.fatigue)*.18
  case 'victory': return t.confidence*.7+t.arousal*.22
  case 'defeat': return t.pain*.35+t.fatigue*.4+(1-t.confidence)*.48
  case 'unknown': return t.curiosity*.7+t.socialWarmth*.25+(1-t.threat)*.16
  default:return 0
 }
}
function lexicalStyleScore(name:string,t:MotorThoughtState){
 const n=name.toLowerCase()
 let score=0
 if(/jab|quick|fast|snap|precision|thrust/.test(n))score+=t.focus*.34+t.urgency*.16
 if(/smash|power|rage|slam|uppercut|crusher/.test(n))score+=t.arousal*.3+t.confidence*.22+t.urgency*.24
 if(/dodge|evade|roll|sidestep|backstep/.test(n))score+=t.threat*.42+Math.abs(t.turn01)*.28
 if(/spin|twirl|flourish|dance|feint/.test(n))score+=t.curiosity*.42+t.arousal*.18
 if(/cast|magic|spell|burst|ultimate/.test(n))score+=t.arousal*.28+t.focus*.24
 if(/guard|block|parry|deflect/.test(n))score+=t.threat*.3+t.focus*.24
 if(/idle|breath|ready|stance|stand/.test(n))score+=(1-t.urgency)*.15+(1-t.speed01)*.16
 if(/run|sprint|dash/.test(n))score+=t.speed01*.18+t.urgency*.1
 return score
}

export function rankThoughtAnimationCandidates(
 names:readonly string[],state:Combatant,fighterId:string,motion:AnimationMotionContext,
 thought:Partial<MotorThoughtState>,actionOrdinal=0,previousClip?:string|null,
):ThoughtAnimationCandidate[]{
 const desired=animationSemanticForState(state,motion)
 const t=normalizeThought(thought),signature=thoughtAnimationSignature(t)
 const catalogue=catalogueAnimationClips(names,fighterId)
 const exact=catalogue.filter(candidate=>candidate.semantic===desired)
 const compatiblePool=catalogue.filter(candidate=>compatible(desired,candidate.semantic))
 const retreatPool=desired==='backward'&&exact.length===0
  ? catalogue.filter(candidate=>candidate.semantic==='walk'||candidate.semantic==='run'||candidate.semantic==='backward')
  : []
 const dedicatedRetreat=retreatPool.filter(candidate=>candidate.name.includes('__backlane_'))
 const retreatFallback=dedicatedRetreat.length>0?dedicatedRetreat:retreatPool
 const depthFallback=desired==='depth'&&exact.length===0
  ? catalogue.filter(candidate=>candidate.semantic==='walk'||candidate.semantic==='run'||candidate.semantic==='dash')
  : []
 const airFallback=airSemantics.has(desired)&&exact.length===0
  ? catalogue.filter(candidate=>desired==='land'
    ? ['air','jump','idle','walk'].includes(candidate.semantic)
    : desired==='dive'
      ? ['air','jump','flight','special','run'].includes(candidate.semantic)
      : ['air','jump','flight','run'].includes(candidate.semantic))
  : []
 const evasiveOverride=desired==='idle'&&t.threat>=.72&&Math.abs(t.turn01)>=.6
 const pool=(locomotionSemantics.has(desired)||airSemantics.has(desired))&&exact.length>0&&!evasiveOverride?exact:retreatFallback.length>0?retreatFallback:depthFallback.length>0?depthFallback:airFallback.length>0?airFallback:compatiblePool
 return pool.map(candidate=>{
  const affinity=semanticAffinity(desired,candidate.semantic)
  const thoughtScore=semanticThoughtScore(candidate.semantic,t)
  const styleScore=lexicalStyleScore(candidate.name,t)
  const sameAsPrevious=previousClip===candidate.name&&pool.length>1
  const continuityBias=sameAsPrevious
   ? (locomotionSemantics.has(desired)||defenseSemantics.has(desired))
     ? .11*(1-t.urgency)*(1-t.threat)+.035*t.focus
     : -(.11+.08*t.curiosity+.04*t.urgency)
   : 0
  const jitter=unitHash(`${fighterId}|${candidate.name}|${signature}|${actionOrdinal}`)*.055
  const score=affinity+thoughtScore+styleScore+candidate.score*.001+jitter+continuityBias
  return{...candidate,score,affinity,thoughtScore,styleScore}
 }).sort((a,b)=>b.score-a.score||a.name.localeCompare(b.name))
}
function loopContract(semantic:AnimationSemantic){
 return ['idle','walk','backward','run','dash','depth','air','flight','block','parry'].includes(semantic)
}

export function planThoughtDrivenAnimation(
 names:readonly string[],state:Combatant,fighterId:string,motion:AnimationMotionContext,
 thought:Partial<MotorThoughtState>,actionOrdinal=0,previousClip?:string|null,
):ThoughtAnimationPlan|null{
 if(!names.length)return null
 const desired=animationSemanticForState(state,motion)
 const inventory=catalogueAnimationClips(names,fighterId)
 const ranked=rankThoughtAnimationCandidates(names,state,fighterId,motion,thought,actionOrdinal,previousClip)
 if(!ranked.length)return null
 const t=normalizeThought(thought),signature=thoughtAnimationSignature(t),top=ranked[0].score
 const band=ranked.filter(candidate=>candidate.score>=top-.16)
 const seed=hash32(`${fighterId}|${desired}|${signature}|${state.comboCount}|${actionOrdinal}`)
 const chosen=band[seed%band.length]
 const strongestAlternative=ranked.find(candidate=>candidate.name!==chosen.name)
 const decisionMargin=strongestAlternative?Math.max(0,chosen.score-strongestAlternative.score):1
 const decisionConfidence=strongestAlternative?clamp(.5+decisionMargin*1.4):1
 const previous=previousClip?inventory.find(candidate=>candidate.name===previousClip):undefined
 const continuityMode:ThoughtAnimationPlan['continuityMode']=chosen.name===previousClip?'hold':previous?.semantic===chosen.semantic?'vary':'switch'
 const locomotion=locomotionSemantics.has(desired)
 const speedScale=locomotion?.82+t.speed01*.34:1
 const activation=1+(t.urgency*.065+t.arousal*.045)-(t.fatigue*.085+t.pain*.055)
 const playbackRate=Math.max(.74,Math.min(1.2,speedScale*activation))
 const calm=(1-t.urgency)*.04+t.focus*.025
 const crossFadeSeconds=Math.max(.05,Math.min(.16,.075+calm-(t.threat*.014)))
 const reverse=desired==='backward'&&chosen.semantic!=='backward'
 return{
  clip:chosen.name,semantic:desired,playbackRate,crossFadeSeconds,loop:loopContract(desired),reverse,
  candidateCount:ranked.length,seed,selectionMode:'thought',thoughtSignature:signature,
  thoughtScore:chosen.score,decisionMargin,decisionConfidence,continuityMode,
  compatibleCount:ranked.length,inventoryCount:inventory.length,chosenSemantic:chosen.semantic,
 }
}
